/**
 * What `GET /api/v1/admin/dashboard/kpis` and `/trends` answer (lot K2, `platform-kpi.types.ts` on the API side). Mirrored, not shared:
 * the same "mirror, don't couple" posture the two dashboards keep for their other types. Amounts are TND, counts are integers.
 */

export interface DashboardFinancial {
  readonly salesTotal: number;
  readonly revenueToday: number;
  readonly revenueMonth: number;
  readonly revenueYear: number;
  readonly averageBasket: number;
  readonly ordersTotal: number;
  readonly ordersToday: number;
  readonly ordersCancelled: number;
}

export interface DashboardMerchants {
  readonly total: number;
  readonly active: number;
  readonly pending: number;
  readonly suspended: number;
  readonly refused: number;
  readonly unassigned: number;
  readonly pendingOver7d: number;
  readonly withoutOwner: number;
  readonly withoutProduct: number;
  readonly withStore: number;
  readonly neverSignedIn: number;
  readonly provisionalExpired: number;
}

export interface DashboardAdmin {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly status: string;
  readonly lastLoginAt: string | null;
  readonly managed: number;
  readonly active: number;
  readonly pending: number;
  readonly suspended: number;
  readonly refused: number;
  readonly revenue: number;
  readonly orders: number;
}

export type ToHandleReason = 'unassigned' | 'provisional_expired' | 'pending_over_7d';

export interface DashboardKpis {
  /** When the figures were computed (up to 60 seconds ago when served from the cache). */
  readonly generatedAt: string;
  readonly timezone: string;
  readonly dayStart: string;
  /** Whether this answer came from the server's 60-second cache. */
  readonly cached: boolean;
  readonly financial: DashboardFinancial;
  readonly merchants: DashboardMerchants;
  readonly admins: {
    readonly total: number;
    readonly active: number;
    readonly disabled: number;
    readonly perAdmin: readonly DashboardAdmin[];
  };
  readonly products: {
    readonly total: number;
    readonly active: number;
    readonly outOfStock: number;
    readonly topSold: readonly {
      readonly productId: string;
      readonly name: string;
      readonly tenantId: string;
      readonly merchantName: string | null;
      readonly quantity: number;
      readonly revenue: number;
    }[];
  };
  readonly security: {
    readonly superadmins: number;
    readonly platformAdmins: number;
    readonly loginsToday: number;
    readonly loginsTodayPlatform: number;
    readonly failedToday: number;
    readonly failedTodayPlatform: number;
    readonly throttledToday: number;
    readonly lockedAccounts: number;
    readonly pendingPromotions: number;
    readonly recentReassignments: {
      readonly last7d: number;
      readonly last30d: number;
      readonly latest: readonly {
        readonly tenantId: string;
        readonly name: string | null;
        readonly toAdminEmail: string;
        readonly at: string;
        readonly byEmail: string | null;
      }[];
    };
  };
  readonly topMerchants: readonly {
    readonly tenantId: string;
    readonly name: string | null;
    readonly revenue: number;
    readonly orders: number;
    readonly managerEmail: string | null;
  }[];
  readonly toHandle: {
    readonly total: number;
    readonly items: readonly {
      readonly tenantId: string;
      readonly name: string | null;
      readonly status: string;
      readonly reason: ToHandleReason;
      readonly ageDays: number;
      readonly managerEmail: string | null;
    }[];
  };
}

export type TrendBucket = 'day' | 'month';

export interface TrendPoint {
  /** `YYYY-MM-DD` (the first of the month for a monthly bucket), in the platform's calendar (Africa/Tunis). */
  readonly period: string;
  readonly revenue: number;
  readonly orders: number;
  readonly registrations: number;
  readonly registrationsOperator: number;
}

export interface DashboardTrends {
  readonly bucket: TrendBucket;
  readonly periods: number;
  readonly timezone: string;
  readonly cached: boolean;
  readonly series: readonly TrendPoint[];
}

/** The ranges the screen offers for the curves. */
export type TrendRangeId = '7d' | '30d' | '90d' | '12m';

export const TREND_RANGES: Readonly<
  Record<TrendRangeId, { readonly bucket: TrendBucket; readonly periods: number }>
> = {
  '7d': { bucket: 'day', periods: 7 },
  '30d': { bucket: 'day', periods: 30 },
  '90d': { bucket: 'day', periods: 90 },
  '12m': { bucket: 'month', periods: 12 },
};
